import { ScrambleText } from '../components/ui/ScrambleText';
import BigShift from './BigShift';
import WhoWeAre from './WhoWeAre';
import Neo from './Neo';
import JoinUs from './JoinUs';
import Careers from './Careers';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useEffect, useState, Suspense } from 'react';
import { ChevronDown } from '../components/ui/ChevronDown';
import { Canvas } from '@react-three/fiber';
// import { DistortedSphere } from '../components/ui/DistortedSphere';
import { FluidDistortedSphere, FluidNoiseScene } from '../components/ui/FluidDistortedSphere';
import SectionIndicator from '../components/ui/SectionIndicator';

const HomePage = () => {
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState('landing');

  // Define sections array consistently
  const sections = ['landing', 'big-shift', 'who-we-are', 'join-us', 'careers']; // Updated to reflect commented out Neo

  const scrollToNextSection = (currentId) => {
    const mainContainer = document.getElementById('main-container');
    // Use the defined sections array
    const currentIndex = sections.indexOf(currentId);
    const nextSection = sections[currentIndex + 1];

    if (nextSection) {
      const element = document.getElementById(nextSection);
      if (element && mainContainer) {
        mainContainer.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth'
          
        });
        // Update current section immediately for faster UI feedback
        setCurrentSection(nextSection);
      }
    }
  };

  // Function to scroll to a specific section by ID
  const scrollToSection = (sectionId) => {
    const mainContainer = document.getElementById('main-container');
    const element = document.getElementById(sectionId);

    if (element && mainContainer) {
      mainContainer.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth'
      });
      // Optionally, update the current section immediately for faster UI feedback
      setCurrentSection(sectionId); // The scroll handler will eventually update it too
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      const mainContainer = document.getElementById('main-container');
      const element = document.getElementById(location.state.scrollTo);

      if (element && mainContainer) {
        setTimeout(() => {
          mainContainer.scrollTo({
            top: element.offsetTop,
            behavior: 'smooth'
          });
          // Update current section if navigating directly
          setCurrentSection(location.state.scrollTo);
        }, 100);
      }
    }
  }, [location.state]);

  // Add scroll event listener to track current section
  useEffect(() => {
    const mainContainer = document.getElementById('main-container');
    if (!mainContainer) return;

    const handleScroll = () => {
      // Use the defined sections array
      const mainContainer = document.getElementById('main-container');
      // Adjust scroll trigger point slightly for better accuracy (e.g., 1/3 down the viewport)
      const scrollPosition = mainContainer.scrollTop + 10; // Use a small offset for earlier activation

      let activeSection = sections[0]; // Default to the first section
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (!element) continue;

        const offsetTop = element.offsetTop;
        const offsetBottom = offsetTop + element.offsetHeight;

        // Check if the trigger point is within the section bounds
        if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
          activeSection = sectionId;
          break; // Found the active section
        }

        // Handle edge case where the last section is scrolled almost fully into view
        if (scrollPosition >= offsetBottom && sectionId === sections[sections.length - 1]) {
          activeSection = sectionId;
        }
      }
      setCurrentSection(activeSection);
    };

    // Debounce scroll handler for performance
    let scrollTimeout;
    const debouncedScrollHandler = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50); // Adjust debounce delay as needed
    };

    mainContainer.addEventListener('scroll', debouncedScrollHandler);
    // Initial check in case the page loads scrolled
    handleScroll();

    return () => {
      clearTimeout(scrollTimeout);
      mainContainer.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [sections]); // Add sections as dependency

 return (
  <main className="relative h-screen bg-black">

    <div
      className="relative h-full snap-container snap-y snap-mandatory overflow-y-auto"
      id="main-container"
    >
      <section
        id="landing"
        className="h-screen pb-24 w-full snap-start flex flex-col items-center justify-center relative -mt-4"
      >
        {/* 3D canvas */}
        <div className="absolute inset-0 z-0">
          <FluidNoiseScene enableControls={true} developmentMode={false} />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40 z-[5]" />
        {/* Content */}
        <div className="relative z-10 max-w-[90rem] w-full mx-auto text-center px-5">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <h1 className="text-5xl sm:text-6xl xl:text-7xl font-bold font-mono mb-4 tracking-tighter leading-tight heading-highlight">
              <ScrambleText text="Moving Humans Forward" className="text-white" />
            </h1>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          >
            <p className="text-base sm:text-lg md:text-xl lg:text-xl mb-8 text-white font-mono whitespace-nowrap pt-2 text-shadow">
              <ScrambleText
                text="We are pioneering multi-disciplinary applied research to discover new frontiers of AI"
                delay={200}
              />
            </p>
          </motion.div>
        </div>
      </section>

      <BigShift id="big-shift" />
      <WhoWeAre id="who-we-are" />
      <JoinUs id="join-us" />
      <Careers id="careers" />
    </div>

    {currentSection !== "landing" && (
      <SectionIndicator
        sections={sections}
        currentSection={currentSection}
        scrollToSection={scrollToSection}
      />
    )}

    {currentSection !== "careers" && (
      <ChevronDown
        onClick={() => scrollToNextSection(currentSection)}
        currentSection={currentSection}
      />
    )}
  </main>
);
};

export default HomePage;
